import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { useAPIGetPredikatKinerjaList } from 'api/kinerja/useAPIKinerja'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import PanelInfo from 'app/KinerjaPage/pages/PelaksanaanV2/components/PanelInfo'
import { PERFORMANCE_PRACTICE_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/performancePractice'
import { PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'
import { WORK_BEHAVIOR_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/workBehavior'
import { Header } from 'app/KinerjaShared/components/Header'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { getSatuanPendidikanByNpsn } from 'app/KinerjaShared/utils/profile'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'

interface RiwayatPenetapanPredikatKinerjaProps {
  npsn?: string
}

export function RiwayatPenetapanPredikatKinerja({
  npsn,
}: RiwayatPenetapanPredikatKinerjaProps) {
  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const { profile, isLoading: isLoadingProfile } = useRoleContext()
  const { isDemoMode } = useDemoContext()

  const { isLoading, data, isSuccess, isError } = useAPIGetPredikatKinerjaList({
    params: { npsn },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
    isDemoMode,
  })

  // Filter out non-submitted Guru. `ableSubmit` = false means the predikat is sent to Guru.
  const resolvedList =
    data?.data?.completedUser?.filter((user) => !user.ableSubmit) ?? []

  const isListError = !isLoading && isError
  const hasListData = !isListError && isSuccess && resolvedList.length > 0

  return (
    <>
      <main className="px-8 pb-8 pt-12 bg-gray-10 min-h-full print:bg-white print:p-0">
        <div className="max-w-7xl mx-auto space-y-5 print:space-y-0">
          <Header
            title="Penetapan Predikat Kinerja"
            className="mb-9 print:mb-0 print:space-x-0"
            hideOnPrint
            rightNode={
              <PanelInfo
                isLoading={isLoadingProfile}
                label="Satuan Pendidikan"
                value={getSatuanPendidikanByNpsn(profile, npsn, isDemoMode)}
              />
            }
          />

          <section className="bg-white rounded-lg">
            <div className="p-6 flex items-start gap-4 print:px-0">
              {/* Title */}
              <div className="space-y-2.5 flex-1">
                <Text variant="heading-md">
                  Berikut data yang sudah dikirim:
                </Text>
                <Text color="subdued">
                  Data sudah ditampilkan pada Hasil Penilaian Kinerja Guru bulan{' '}
                  <CardPeriodType withYear={false} />.
                </Text>
              </div>
              <div className="flex-none flex flex-row gap-2.5 print:hidden">
                <Button
                  size="lg"
                  color="white"
                  type="button"
                  className="space-x-1"
                  // disabled={isLoadingPelaksanaanProgress || isLoadingTargetPerilaku}
                  onClick={() => {
                    trackEvent({
                      activity_type: 'penilaian_ks_pk_click_cetak',
                      screen_name: 'ks_penetapan_kinerja',
                      event_trigger: 'ks_penilaian_hub',
                    })

                    window.print()
                  }}
                >
                  <Icon>print</Icon> <span>Cetak</span>
                </Button>
              </div>
            </div>
            <TablePelaksanaanKinerja className="print:mt-0">
              <TableHeader>
                <TableHeaderItem>
                  <Text variant="body" color="default" className="!font-bold">
                    Nama
                  </Text>
                </TableHeaderItem>
                <TableHeaderItem>
                  <Text variant="body" color="default" className="!font-bold">
                    Observasi Kelas
                  </Text>
                </TableHeaderItem>
                <TableHeaderItem>
                  <Text variant="body" color="default" className="!font-bold">
                    Perilaku Kerja
                  </Text>
                </TableHeaderItem>
                <TableHeaderItem>
                  <Text variant="body" color="default" className="!font-bold">
                    Predikat Kinerja
                  </Text>
                </TableHeaderItem>
              </TableHeader>
              <TableBody>
                {/* If loading */}
                {isLoading && <TableShimmer columns={5} />}

                {(isListError || !hasListData) && (
                  <TableRowStates
                    type={isListError ? 'ERROR' : 'EMPTY'}
                    colSpan={5}
                    children={null}
                  />
                )}

                {hasListData &&
                  resolvedList.map((item, idx) => {
                    return (
                      <TableRow key={`ks_${item.ptkId}_${idx}`}>
                        <TableRowItem className="overflow-wrap-anywhere align-middle">
                          <Text variant="body-sm" color="default">
                            {item?.name || ''}
                          </Text>
                        </TableRowItem>
                        <TableRowItem className="overflow-wrap-anywhere align-middle">
                          <Text variant="body-sm" color="default">
                            {PERFORMANCE_PRACTICE_RESULT_TEXT[
                              item?.performancePractice?.result
                            ] || ''}
                          </Text>
                        </TableRowItem>
                        <TableRowItem className="overflow-wrap-anywhere align-middle">
                          <Text variant="body-sm" color="default">
                            {WORK_BEHAVIOR_RESULT_TEXT[
                              item?.workBehavior?.result
                            ] || ''}
                          </Text>
                        </TableRowItem>
                        <TableRowItem className="overflow-wrap-anywhere align-middle">
                          <Text variant="body-sm" color="default">
                            {PREDICATE_RECOMMENDATION_RESULT_TEXT[
                              item?.predicateRecommendation
                            ] || ''}
                          </Text>
                        </TableRowItem>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </TablePelaksanaanKinerja>
          </section>
        </div>
      </main>
    </>
  )
}
